import './rater.endorsements.list.detail.scss';

import {
  AnswerType,
  IUnderwritingCoverage,
  IUnderwritingEndorsement,
  MediaEventCoverageOption,
  NoteTargetName,
  NoteTargetType,
  QuestionType,
  SaveAnswerInput,
  TargetAreaType,
  useUpsertAnswersMutation,
} from '@converge/graphql';
import { Box, FormLabel, TableCell, TableRow, Typography } from '@mui/material';
import {
  CircularLoader,
  ConvergeTooltip,
  FormToggleSwitchField,
  NewFromSelectField,
} from '../../../../presentation';
import {
  EndorsementUuid,
  TEMP_ENDORSEMENT_SIMILAR_PREMIUM_BEARING,
  numberFieldMaxValue,
} from '@converge/data';
import {
  FieldErrors,
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import {
  FusionScoreModifierDisplay,
  RaterNotesButton,
  RaterNotesRow,
} from '../common';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  formatDollarAmount,
  handleError,
  limitNumberToTwoDigits,
} from '@converge/utils';
import {
  getAnswersFromFormValues,
  getFormValuesFromAnswersNested,
} from '../../../application';
import {
  getModifiedPremiumFromEndorsement,
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from '../data';

import { ApplicationQuestion } from '../../../application/question/application-question';
import { CascadeLimitAndRetentionButton } from './cascade-limit-and-retention.button';
import { NumberField } from '../common/number.field';
import { RaterCoverageListDetail } from './list-detail/rater.coverage.list.detail';
import { RaterCoverageListDetailExpandedCells } from './list-detail/rater.coverage.list.expanded.cells';
import { RaterRetroactiveDate } from './rater.retroactive-date';
import { Stack } from '@mui/system';
import config from '@converge/config';
import debounce from 'lodash.debounce';
import { getParsedValue } from './list-detail/utils';
import { sentenceCase } from 'change-case';
import { shouldRenderPrimaryEndorsementQuestions } from './rater.endorsements.list.detail.questions-visibility';
import { shouldRenderRetroactiveDate } from './rater.endorsements.list.detail.retroactive-date-visibility';
import { useConvergeSnackBar } from '../../../../contexts';
import { useUnderwritingSubmissionStateContext } from '../../underwriting.submission.context';

export interface IRaterEndorsementListDetailProps {
  endorsement: IUnderwritingEndorsement;
  setOpenSaveDialog: (flag: boolean) => void;
  isLastRow: boolean;
  isEditable: boolean;
  quoteLimit?: number | null;
  quoteRetention?: number | null;
  answers: SaveAnswerInput[];
  coverages: IUnderwritingCoverage[];
  updateAnswers: (
    quoteUuid: string | null | undefined,
    answers: SaveAnswerInput[]
  ) => void;
  mode: 'excess' | 'primary';
  parentCoverage: (
    endorsementCoverageUuid: string
  ) => IUnderwritingCoverage | undefined;
  conflictError?: string;
  onToggleIncluded?: (uuid: string, included: boolean) => void;
}

const RaterEndorsementListDetailContext =
  createContext<IRaterEndorsementListDetailProps | null>(null);

const useRaterEndorsementListDetailContext = () => {
  const context = useContext(RaterEndorsementListDetailContext);
  if (!context) {
    throw new Error(
      `This component should be within RaterEndorsementListDetail`
    );
  }
  return context;
};

export const RaterEndorsementListDetail = (
  props: IRaterEndorsementListDetailProps
) => {
  const {
    coverages,
    endorsement,
    mode,
    quoteLimit,
    quoteRetention,
    parentCoverage,
    endorsement: {
      name,
      uuid,
      features,
      hasChanged,
      coverageUuid,
      // showRetroactiveDateFields,
      // showMediaEventCoverageOptionField,
      nonPremiumBearing,
      quote: {
        included: includedInPrimary,
        basePremium,
        retention,
        limit,
        modifiedCoveragePremium,
        ratedFusionScoreModifier,
        fusionScoreModifier,
        minFusionScoreModifier,
        maxFusionScoreModifier,
        technicalPremium,
        fusionScore,
        hazardGroup,
        uuid: quoteUuid,
        fullPriorActs,
      } = {},
      questions,
    } = {},
    isLastRow,
  } = props;

  const { dispatch, actions } = useRaterActionDispatchContext();
  const treatAsPremiumBearing =
    TEMP_ENDORSEMENT_SIMILAR_PREMIUM_BEARING.includes(endorsement.uuid || '');
  const [retentionLive, setRetentionLive] = useState(retention ?? null);
  const [limitLive, setLimitLive] = useState(limit ?? null);
  const {
    isRequestingRatingUpdate,
    showExpandedView,
    columnCount,
    isEditable,
    upsertIsLoading,
    inForcePolicy,
  } = useRaterStateContextSelector(
    ({
      isRequestingRatingUpdate,
      showExpandedView,
      columnCount,
      isEditable,
      upsertQuoteIsLoading,
      upsertAnswersIsLoading,
      quote: { uuid: raterQuoteUuid, inForcePolicy },
    }) => {
      return {
        raterQuoteUuid,
        isRequestingRatingUpdate,
        showExpandedView,
        columnCount,
        isEditable,
        upsertQuoteIsLoading,
        upsertAnswersIsLoading,
        upsertIsLoading: upsertQuoteIsLoading || upsertAnswersIsLoading,
        inForcePolicy,
      };
    }
  );

  const {
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const isRetroactiveDateOrFullPriorActs = useMemo(
    () => shouldRenderRetroactiveDate(uuid),
    [uuid]
  );

  const {
    info: { submission },
  } = useUnderwritingSubmissionStateContext();

  const submissionStartDate =
    submission && 'policyStartDate' in submission
      ? submission?.policyStartDate
      : '';

  const retroactiveDate =
    endorsement?.quote?.retroactiveDate ?? submissionStartDate;
  const [selectedRetroActiveDate, setSelectedRetroActiveDate] = useState<
    Date | string | null
  >(retroactiveDate);

  // Sync the local state when the reducer's endorsement.quote.retroactiveDate
  // changes (e.g. after upsertQuote completes and the reducer back-fills the
  // policy start date for TE&O via updateAfterQuoteUpsert). Without this the
  // popover keeps rendering the initial value picked by useState.
  const reducerRetroactiveDate = endorsement?.quote?.retroactiveDate ?? null;
  useEffect(() => {
    if (reducerRetroactiveDate) {
      setSelectedRetroActiveDate(reducerRetroactiveDate);
    }
  }, [reducerRetroactiveDate]);

  const included = includedInPrimary;

  const isParentCoverageIncluded =
    !coverageUuid || parentCoverage(coverageUuid)?.quote?.included;

  const isReadOnly =
    isRequestingRatingUpdate ||
    upsertIsLoading ||
    !included ||
    !isEditable ||
    inForcePolicy ||
    false;

  const hasInvalidSelectedFusionScoreModifier =
    errors &&
    errors['selectedFusionScoreModifier'] &&
    name &&
    name in errors['selectedFusionScoreModifier']
      ? true
      : false;

  const questionsToRender = useMemo(() => {
    const exampleQuestion: IUnderwritingEndorsement['questions']['edges'][0] = {
      node: {
        type: QuestionType.Date,
        question: 'What is your favorite date?',
        sectionUuid: endorsement.uuid ?? 'x',
        subSectionUuid: endorsement.uuid ?? 'x',
        uuid: endorsement.uuid ?? 'x',
        groupUuid: null,
        answer: {
          type: AnswerType.Date,
          uuid: 'a',
          answer: new Date().toISOString(),
        },
      },
    };

    const questionsToRender = questions?.edges ? [...questions.edges] : [];

    if (config.featureFlags.mockEndorsementQuestions) {
      questionsToRender?.push(exampleQuestion);
    }

    return questionsToRender.sort(
      (a, b) => (a.node.order ?? 0) - (b.node.order ?? 0)
    );
  }, [endorsement.uuid, questions?.edges]);

  const defaultFormValues = useMemo(
    () =>
      getFormValuesFromAnswersNested(
        {
          edges: questionsToRender,
        }
        // `${quoteUuid}:${raterQuoteUuid}`
      ),
    [questionsToRender]
  );

  const formMethods = useForm({
    defaultValues: defaultFormValues,
  });

  const { upsertAnswers, saveAnswersIsLoading, saveAnswersError } =
    useUpsertAnswersMutation();

  useEffect(() => {
    dispatch(
      actions.setUpsertAnswersState({
        loading: saveAnswersIsLoading,
        error: saveAnswersError,
      })
    );
  }, [actions, dispatch, saveAnswersError, saveAnswersIsLoading]);

  const { notify, closeSnackbar } = useConvergeSnackBar();

  const endorsementName = endorsement.name;

  const checkForNullValue = useCallback(
    (fieldName: string, value?: number | null) => {
      if (!value) {
        setError(`${fieldName}.${endorsementName}`, {
          type: fieldName,
          message: `${fieldName} must be greater than 0`,
        });
        return true;
      }
      return false;
    },
    [setError, endorsementName]
  );

  const hasErrors = (fieldName: string) => {
    return errors &&
      errors[fieldName] &&
      endorsementName &&
      endorsementName in (errors[fieldName] as FieldErrors<FieldValues>)
      ? true
      : false;
  };

  const getErrorMessage = (fieldName: string): string | undefined => {
    const fieldError = errors?.[fieldName];

    if (!fieldError || !endorsementName) return undefined;

    const maybeError = (fieldError as FieldErrors<FieldValues>)[
      endorsementName
    ];

    return typeof maybeError?.message === 'string'
      ? maybeError.message
      : undefined;
  };

  const hasInvalidLimit = hasErrors('limit');
  const hasInvalidRetention = hasErrors('retention');
  const limitErrorMessage = getErrorMessage('limit');
  const retentionErrorMessage = getErrorMessage('retention');

  const hasInvalidData =
    hasInvalidLimit ||
    hasInvalidRetention ||
    hasInvalidSelectedFusionScoreModifier;

  useEffect(() => {
    dispatch(actions.setIsInvalidRaterData(hasInvalidData));
  }, [actions, dispatch, hasInvalidData]);

  useEffect(() => {
    const scoreMofifier = endorsement?.quote?.selectedFusionScoreModifier;
    if (
      endorsement.uuid ===
        EndorsementUuid.TechnologyErrorsAndOmissionsEndorsement ||
      endorsement.uuid === EndorsementUuid.MediaEventLiabilityEndorsement
    ) {
      if (scoreMofifier && scoreMofifier < (minFusionScoreModifier || 0)) {
        setError(`selectedFusionScoreModifier.${name}`, {
          type: 'FusionScoreModifier',
          message: `Fusion Score modifier must be a number between ${
            minFusionScoreModifier ?? 0
          } and ${maxFusionScoreModifier ?? 100}`,
        });
        return;
      }
    } else if (
      endorsement.uuid !==
        EndorsementUuid.TechnologyErrorsAndOmissionsEndorsement &&
      endorsement.uuid !== EndorsementUuid.MediaEventLiabilityEndorsement
    ) {
      if (
        scoreMofifier &&
        (scoreMofifier < (minFusionScoreModifier || 0) ||
          scoreMofifier > (maxFusionScoreModifier || 0))
      ) {
        setError(`selectedFusionScoreModifier.${name}`, {
          type: 'FusionScoreModifier',
          message: `Fusion Score modifier must be a number between ${
            minFusionScoreModifier ?? 0
          } and ${maxFusionScoreModifier ?? 100}`,
        });
        return;
      }
    }
  }, [
    endorsement?.quote?.selectedFusionScoreModifier,
    endorsement.uuid,
    maxFusionScoreModifier,
    minFusionScoreModifier,
    limit,
    endorsementName,
    name,
    setError,
  ]);

  const specialEndorsementUuids: string[] = [
    EndorsementUuid.CyberEventAmendatory,
    EndorsementUuid.MissedBidAmendatory,
    EndorsementUuid.TrackingTechnologyClaimsExpenseSublimit,
  ];

  // const {limitValue} = { limitValue: endorsement.quote?.[limitField as unknown as 'limit']}
  const handleUpdateLimit = (newValue: number | null) => {
    if (checkForNullValue('limit', newValue)) {
      return;
    }

    if (coverageUuid && newValue) {
      const parent = parentCoverage(coverageUuid);
      const parentLimit = parent?.quote?.limit;

      if (parentLimit && newValue > parentLimit) {
        setError(`limit.${endorsementName}`, {
          type: 'limit',
          message: `Amount cannot exceed the ${formatDollarAmount(
            parentLimit
          )} parent limit`,
        });
        return;
      }
    }

    clearErrors(`limit.${endorsementName}`);
    dispatch(
      actions.updateEndorsement({
        ...endorsement,
        quote: {
          ...endorsement.quote,
          limit: newValue,
        },
        update: true,
      })
    );
  };

  const handleUpdateRetention = (newValue: number | null) => {
    if (checkForNullValue('retention', newValue)) {
      return;
    }

    const shouldValidateParentRetention = specialEndorsementUuids.includes(
      endorsement?.uuid || ''
    );

    if (shouldValidateParentRetention && coverageUuid && newValue) {
      const parent = parentCoverage(coverageUuid);
      const parentRetention = parent?.quote?.retention;

      if (parentRetention && newValue < parentRetention) {
        setError(`retention.${endorsementName}`, {
          type: 'retention',
          message: `Amount cannot be less than the ${formatDollarAmount(
            parentRetention
          )} parent retention`,
        });
        return;
      }
    }

    clearErrors(`retention.${endorsementName}`);
    dispatch(
      actions.updateEndorsement({
        ...endorsement,
        quote: {
          ...endorsement.quote,
          retention: newValue,
        },
        update: true,
      })
    );
  };

  const handleUpdateFusionScoreModifier = (newValue: number | null) => {
    const parsedValue = getParsedValue(newValue);

    if (
      parsedValue !== null &&
      parsedValue !== undefined &&
      parsedValue < (minFusionScoreModifier || 0)
    ) {
      setError(`selectedFusionScoreModifier.${name}`, {
        type: 'FusionScoreModifier',
        message: `Fusion Score modifier must be a number between ${
          minFusionScoreModifier ?? 0
        } and ${maxFusionScoreModifier ?? 100}`,
      });
      return;
    }
    if (
      // endorsement.uuid !==
      //   EndorsementUuid.TechnologyErrorsAndOmissionsEndorsement &&
      // endorsement.uuid !== EndorsementUuid.MediaEventLiabilityEndorsement &&
      parsedValue !== null &&
      parsedValue !== undefined &&
      parsedValue > (maxFusionScoreModifier || 100)
    ) {
      setError(`selectedFusionScoreModifier.${name}`, {
        type: 'FusionScoreModifier',
        message: `Fusion Score modifier must be a number between ${
          minFusionScoreModifier ?? 0
        } and ${maxFusionScoreModifier ?? 100}`,
      });
      return;
    }

    clearErrors(`selectedFusionScoreModifier.${name}`);
    dispatch(
      actions.updateEndorsement({
        ...endorsement,
        quote: {
          ...endorsement.quote,
          selectedFusionScoreModifier: parsedValue,
          fusionScoreModifier: parsedValue,
        },
        update: true,
      })
    );
  };

  const submit = useCallback(
    async (quoteUuid: string | null | undefined) => {
      if (!quoteUuid) {
        handleError({
          message: `Failed to save ${name}: No endorsement.quote.uuid`,
          source: 'RaterEndorsementListDetail',
          context: {
            data: {
              endorsement,
            },
          },
        });
        notify({
          message: <>Failed to save {name}: No endorsement quote UUID</>,
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        });
        return;
      }
      try {
        const notification = notify({
          message: <>Saving {name}...</>,
          variant: 'info',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        });

        const values = getAnswersFromFormValues(
          { edges: questionsToRender },
          formMethods.getValues()
        );

        await upsertAnswers({
          variables: {
            data: {
              // TODO: Remove the fallback after API ticket CON-1589 is done (https://convergeins.atlassian.net/browse/CON-1589)
              targetArea: TargetAreaType.Endorsement,
              targetUuid: quoteUuid ?? '',
              answers: values,
            },
          },
        });

        closeSnackbar(notification);

        notify({
          message: <>Saved {name}.</>,
          variant: 'success',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        });
      } catch (cause) {
        handleError({
          cause,
          source: 'Endorsement rating',
          message: `Failed to save endorsement`,
        });
        notify({
          message: (
            <>
              Failed to save {name}: {`${cause}`}
            </>
          ),
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        });
      }
    },
    [
      name,
      endorsement,
      notify,
      upsertAnswers,
      questionsToRender,
      formMethods,
      closeSnackbar,
    ]
  );

  const debouncedSubmit = useMemo(
    () =>
      debounce((quoteUuid: string) => {
        submit(quoteUuid);
      }, 1000),
    [submit]
  );

  const renderExcessQuestions = (isPrimary: boolean) => (
    <TableRow
      className={`excess-questions-table-row ${
        saveAnswersIsLoading ? 'pulsate-background-color' : undefined
      }`}
      style={{ display: isPrimary === true ? 'flex' : '' }}
    >
      <TableCell
        colSpan={columnCount}
        style={{
          borderBottom: isPrimary ? 'none' : '1px solid rgba(224, 224, 224, 1)',
          flex: isPrimary ? 1 : '',
        }}
      >
        <Stack
          className="table-cell-textArea"
          minHeight={!quoteUuid ? 120 : undefined}
        >
          {!quoteUuid && (
            <Stack className="loading-container">
              <Stack className="loading-area">
                {(upsertIsLoading || isRequestingRatingUpdate) && (
                  <CircularLoader className="circular-loader-style" />
                )}
              </Stack>
            </Stack>
          )}

          <FormProvider {...formMethods}>
            {quoteUuid &&
              questionsToRender.map(({ node: question }) => {
                return (
                  <Stack className="questions-render-area" key={question.uuid}>
                    <ApplicationQuestion
                      isApplicationViewOnly={
                        upsertIsLoading ||
                        !isEditable ||
                        isRequestingRatingUpdate ||
                        !quoteUuid
                      }
                      isPrimary={isPrimary}
                      questions={[question]}
                      onBlur={() => {
                        debouncedSubmit(quoteUuid);
                      }}
                      setRaterError={true}
                    />
                  </Stack>
                );
              })}
          </FormProvider>
        </Stack>
      </TableCell>
    </TableRow>
  );
  const coverageModifiedCoveragePremium = getModifiedPremiumFromEndorsement(
    coverages,
    endorsement
  );

  const renderPremiumBearingFields = () => {
    const getValue = (main?: number | null, fallback?: number | null) =>
      (treatAsPremiumBearing ? main || fallback : main) ?? null;

    const limitValue = getValue(limit, quoteLimit);
    const retentionValue = getValue(retention, quoteRetention);

    const shouldHidePremiumColumns = specialEndorsementUuids.includes(
      endorsement?.uuid || ''
    );

    return (
      <>
        <TableCell>{/* Empty cell for info icon column */}</TableCell>
        <TableCell>
          <Box minWidth={110}>
            <NumberField
              isCurrency
              name={`${name}:limit`}
              value={limitValue}
              max={numberFieldMaxValue}
              error={hasInvalidLimit}
              helperText={limitErrorMessage}
              isDisabled={isReadOnly}
              onChange={(val) => {
                setLimitLive(val);
              }}
              onBlur={handleUpdateLimit}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0,
                  marginRight: 0,
                },
              }}
            />
          </Box>
        </TableCell>
        <TableCell>
          <Box minWidth={110}>
            <NumberField
              isCurrency
              name={`${name}:retention`}
              value={retentionValue}
              max={numberFieldMaxValue}
              error={hasInvalidRetention}
              helperText={retentionErrorMessage}
              isDisabled={isReadOnly}
              onChange={(val) => {
                setRetentionLive(val);
              }}
              onBlur={handleUpdateRetention}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0,
                  marginRight: 0,
                },
              }}
            />
          </Box>
        </TableCell>
        <TableCell>
          {!upsertIsLoading &&
            isEditable &&
            limitLive !== 0 &&
            retentionLive !== 0 &&
            endorsement?.uuid !==
              EndorsementUuid.AdditionalPrivacyEventExpensesOutSideLimit &&
            endorsement?.uuid !==
              EndorsementUuid.ContingentBodilyInjuryAndPropertyDamage &&
            endorsement?.uuid !== EndorsementUuid.BipaClaimsExpensesSublimit &&
            endorsement?.uuid !== EndorsementUuid.TcpaClaimsExpensesSublimit &&
            endorsement?.uuid !== EndorsementUuid.AdaClaimsExpensesSublimit &&
            endorsement?.uuid !== EndorsementUuid.MissedBidAmendatory &&
            endorsement?.uuid !== EndorsementUuid.CyberEventAmendatory &&
            endorsement?.uuid !==
              EndorsementUuid.TrackingTechnologyClaimsExpenseSublimit && (
              <CascadeLimitAndRetentionButton
                limit={limit}
                retention={retention}
                isFirstRow={false}
                isLastRow={isLastRow}
              />
            )}
        </TableCell>
        {showExpandedView && <RaterEndorsementListDetailExpandedCells />}

        {shouldHidePremiumColumns ? (
          <TableCell colSpan={2} aria-label="Premium bearing no value">
            <RaterEndorsementListDetail.Value
              isDisabled={
                isRequestingRatingUpdate ||
                !included ||
                isRequestingRatingUpdate
              }
            >
              <Box className="not-premium-container">
                Premium bearing no value
              </Box>
            </RaterEndorsementListDetail.Value>
          </TableCell>
        ) : (
          <>
            <TableCell aria-label="Base premium">
              <RaterEndorsementListDetail.Value
                isDisabled={
                  isRequestingRatingUpdate ||
                  !included ||
                  isRequestingRatingUpdate
                }
                isBold={included ?? false}
              >
                <Box style={{ fontSize: '16px' }}>
                  {formatDollarAmount(basePremium)}
                </Box>
              </RaterEndorsementListDetail.Value>
            </TableCell>

            <TableCell aria-label="Technical premium">
              <RaterEndorsementListDetail.Value
                isDisabled={
                  isRequestingRatingUpdate ||
                  !included ||
                  isRequestingRatingUpdate
                }
                isBold={included ?? false}
              >
                <Box style={{ fontSize: '16px' }}>
                  {formatDollarAmount(technicalPremium)}
                </Box>
              </RaterEndorsementListDetail.Value>
            </TableCell>
          </>
        )}
        <TableCell>
          <RaterEndorsementListDetail.Value
            isDisabled={isRequestingRatingUpdate || !included}
          >
            <Box style={{ fontSize: '16px' }}>{fusionScore?.toFixed(1)}</Box>
          </RaterEndorsementListDetail.Value>
        </TableCell>

        <TableCell>
          <Stack className="fusionScore-modifier-container" spacing={1}>
            {
              <>
                <Box className="fusionScore-modifier">
                  <NumberField
                    name={`selectedFusionScoreModifier.${name}`}
                    placeholder={ratedFusionScoreModifier?.toFixed(2)}
                    value={
                      fusionScoreModifier
                        ? limitNumberToTwoDigits(fusionScoreModifier)
                        : null
                    }
                    max={maxFusionScoreModifier ?? 100}
                    min={minFusionScoreModifier ?? 0}
                    isDisabled={
                      isRequestingRatingUpdate ||
                      upsertIsLoading ||
                      !included ||
                      !isEditable
                    }
                    onBlur={handleUpdateFusionScoreModifier}
                    error={
                      errors && errors['selectedFusionScoreModifier']
                        ? Object.prototype.hasOwnProperty.call(
                            errors['selectedFusionScoreModifier'],
                            name as string
                          )
                        : false
                    }
                    asValue
                  />
                </Box>

                <FusionScoreModifierDisplay label="Min">
                  {minFusionScoreModifier ?? 'N/A'}
                </FusionScoreModifierDisplay>
                <FusionScoreModifierDisplay label="Max">
                  {maxFusionScoreModifier ?? 'N/A'}
                </FusionScoreModifierDisplay>

                <TableCell className="cascade-button-container">
                  <Stack
                    alignItems={'center'}
                    justifyContent={'center'}
                    className="cascade-button"
                  ></Stack>
                </TableCell>
              </>
            }
          </Stack>
        </TableCell>

        <TableCell aria-label="Modified endorsement premium">
          <RaterEndorsementListDetail.Value
            isDisabled={
              upsertIsLoading || !included || isRequestingRatingUpdate
            }
            isBold={included ?? false}
          >
            {formatDollarAmount(
              coverageModifiedCoveragePremium || modifiedCoveragePremium
            )}
          </RaterEndorsementListDetail.Value>
        </TableCell>
      </>
    );
  };

  return (
    <RaterEndorsementListDetailContext.Provider value={props}>
      {props.conflictError && (
        <TableRow sx={{ background: 'rgb(253, 245, 231, 0.75)' }}>
          <TableCell
            colSpan={columnCount}
            sx={{ py: 0.75, borderBottom: 'none', paddingLeft: '250px' }}
          >
            <Typography
              sx={{ fontSize: '12px', color: '#D32F2F', lineHeight: 1.5 }}
              dangerouslySetInnerHTML={{ __html: props.conflictError }}
            />
          </TableCell>
        </TableRow>
      )}
      <TableRow
        sx={{
          background: hasInvalidData
            ? `rgba(219, 82, 76, ${0.1})`
            : hasChanged
            ? 'rgb(253, 245, 231, 0.75)'
            : undefined,
        }}
      >
        <TableCell className="endorsement-list-name">{name}</TableCell>
        <TableCell className="endorsement-list-hazard">
          <RaterCoverageListDetail.Value
            isDisabled={!included}
            color={
              hazardGroup && hazardGroup >= config.riskyHazardGroup
                ? 'error'
                : undefined
            }
            isBold={
              hazardGroup ? hazardGroup >= config.riskyHazardGroup : false
            }
          >
            {hazardGroup}
          </RaterCoverageListDetail.Value>
        </TableCell>

        {/* Toggle Switch */}
        <TableCell>
          <Stack className="endorsement-list-toggle-container">
            <ConvergeTooltip
              title={
                !isParentCoverageIncluded
                  ? 'Parent coverage must be included'
                  : ''
              }
              disabled={isParentCoverageIncluded || false}
            >
              <span>
                <FormToggleSwitchField.Switch
                  onChange={(event, included) => {
                    dispatch(
                      actions.updateEndorsement({
                        ...endorsement,
                        quote: {
                          ...endorsement.quote,
                          included,
                        },
                        update: true,
                      })
                    );
                    props.onToggleIncluded?.(endorsement.uuid ?? '', included);
                  }}
                  checked={included ?? false}
                  disabled={
                    isRequestingRatingUpdate ||
                    upsertIsLoading ||
                    !isEditable ||
                    inForcePolicy ||
                    !isParentCoverageIncluded
                  }
                />
              </span>
            </ConvergeTooltip>
            {isRetroactiveDateOrFullPriorActs && (
              <Box className="rater-retroactive-date">
                <RaterRetroactiveDate
                  uuid={uuid}
                  isDisabled={isReadOnly}
                  retroactiveDate={selectedRetroActiveDate}
                  fullPriorActs={fullPriorActs}
                  onRetroactiveDateChange={(newRetroactiveDate) => {
                    const date = newRetroactiveDate?.toDate() || null;
                    setSelectedRetroActiveDate(date);
                    dispatch(
                      actions.setEndorsementsRetroactiveDate(uuid, date, false)
                    );
                  }}
                  onFullPriorActsChange={(fullPriorActs) => {
                    dispatch(
                      actions.setEndorsementsRetroactiveDate(
                        uuid,
                        null,
                        fullPriorActs,
                        true
                      )
                    );
                  }}
                />
              </Box>
            )}
          </Stack>
        </TableCell>

        {nonPremiumBearing && !treatAsPremiumBearing ? (
          <>
            <TableCell>{/* Empty cell for info icon column */}</TableCell>
            <TableCell colSpan={columnCount - 5}>
              <Box className="not-premium-container">Non-premium bearing</Box>
            </TableCell>
          </>
        ) : (
          renderPremiumBearingFields()
        )}

        <TableCell aria-label="Notes">
          <RaterNotesButton
            uuid={quoteUuid ?? ''}
            targetName={NoteTargetName.HasEndorsementType}
            targetType={NoteTargetType.Relationship}
          />
        </TableCell>
      </TableRow>
      {shouldRenderPrimaryEndorsementQuestions({
        nonPremiumBearing,
        included,
        questionCount: questions?.edges?.length ?? 0,
      }) && (
        <TableRow>
          <TableCell colSpan={columnCount}>
            {renderExcessQuestions(true)}
          </TableCell>
        </TableRow>
      )}

      {included && features?.enableMediaEventCoverage && (
        <TableRow className="media-event-coverage-option-field-table-row">
          <TableCell colSpan={columnCount}>
            <Stack className="media-event-coverage-option-field-container">
              <MediaEventCoverageOptionField />
            </Stack>
          </TableCell>
        </TableRow>
      )}

      {included &&
        questions?.edges &&
        questions.edges.length > 0 &&
        mode === 'excess' &&
        renderExcessQuestions(false)}

      <RaterNotesRow
        uuid={quoteUuid ?? ''}
        targetName={NoteTargetName.HasEndorsementType}
        targetType={NoteTargetType.Relationship}
        colSpan={columnCount}
      />
    </RaterEndorsementListDetailContext.Provider>
  );
};

//////
interface IRaterEndorsementListDetailValueProps extends PropsWithChildren {
  isDisabled?: boolean;
  isBold?: boolean;
}

RaterEndorsementListDetail.Value = ({
  children,
  isDisabled,
  isBold,
}: IRaterEndorsementListDetailValueProps) => {
  return (
    <span
      style={{
        opacity: isDisabled ? 0.5 : undefined,
        fontWeight: isBold ? 'bold' : undefined,
      }}
    >
      {children}
    </span>
  );
};

const RaterEndorsementListDetailExpandedCells = () => {
  const { endorsement } = useRaterEndorsementListDetailContext();

  const isRequestingRatingUpdate = useRaterStateContextSelector(
    ({ isRequestingRatingUpdate }) => {
      return isRequestingRatingUpdate;
    }
  );

  const {
    quote: {
      included,
      limitRetentionFactor,
      priorActsFactor,
      waitingPeriodFactor,
      restorationPeriodFactor,
      policyTermFactor,
      commissionAdjustmentFactor,
      enhancedMediaFactor,
      sharedCoverageAggregateFactor,
      subCoverageFactor,
      sharedPolicyAggregateFactor,
    } = {},
  } = endorsement;

  return (
    <>
      <RaterCoverageListDetailExpandedCells
        included={included}
        limitRetentionFactor={limitRetentionFactor}
        priorActsFactor={priorActsFactor}
        waitingPeriodFactor={waitingPeriodFactor}
        restorationPeriodFactor={restorationPeriodFactor}
        policyTermFactor={policyTermFactor}
        commissionAdjustmentFactor={commissionAdjustmentFactor}
        isRequestingRatingUpdate={isRequestingRatingUpdate}
        enhancedMediaFactor={enhancedMediaFactor}
        sharedCoverageAggregateFactor={sharedCoverageAggregateFactor}
        sharedPolicyAggregateFactor={sharedPolicyAggregateFactor}
        subCoverageFactor={subCoverageFactor}
      />
    </>
  );
};

const MediaEventCoverageOptionField = () => {
  const {
    endorsement,
    endorsement: {
      uuid,
      quote: { mediaEventCoverageOption },
    },
    isEditable,
  } = useRaterEndorsementListDetailContext();

  const {
    quote: { inForcePolicy },
  } = useRaterStateContextSelector(({ quote }) => {
    return {
      quote,
    };
  });

  const { dispatch, actions } = useRaterActionDispatchContext();

  return (
    <>
      <Box padding={1} marginLeft={2}>
        <FormLabel>Media Event Coverage</FormLabel>
      </Box>
      <Box className="media-event-coverage-option-field">
        <NewFromSelectField
          disabled={!isEditable || inForcePolicy}
          hideLabel
          name={`${uuid}:mediaEventCoverageOption`}
          value={mediaEventCoverageOption}
          onChange={(event) => {
            dispatch(
              actions.updateEndorsement({
                ...endorsement,
                quote: {
                  ...endorsement.quote,
                  mediaEventCoverageOption: event.target
                    .value as MediaEventCoverageOption,
                },
                update: true,
              })
            );
          }}
          options={[
            {
              label: sentenceCase(MediaEventCoverageOption.Enhanced),
              value: MediaEventCoverageOption.Enhanced,
            },
            {
              label: sentenceCase(MediaEventCoverageOption.Standard),
              value: MediaEventCoverageOption.Standard,
            },
          ]}
        />
      </Box>
    </>
  );
};
